import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Users, MonitorPlay, CalendarClock, Clock, PlayCircle, FileText } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { whatsappLink } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";

const WA = whatsappLink("34637570075", "Hola, me interesa el curso intensivo de teórica en Vigo. ¿Cuándo empieza el próximo?");

const STATS = [
  { icon: Zap, k: "1 semana", v: "para alcanzar un nivel alto de teórica" },
  { icon: FileText, k: "Lo que cae", v: "resumimos lo que está cayendo en el examen" },
  { icon: MonitorPlay, k: "24/7", v: "todos los temas grabados en nuestra plataforma" },
  { icon: Clock, k: "A tu ritmo", v: "o a toda velocidad: tú marcas los tiempos" },
];

const MODALIDADES = [
  {
    icon: Users,
    title: "Clases tradicionales",
    sub: "Presencial en nuestros centros",
    points: [
      "Clases con profesor en el aula",
      "Resolución de dudas al momento",
      "Ideal si prefieres una rutina de estudio guiada",
    ],
    highlight: false,
  },
  {
    icon: Zap,
    title: "Intensivo de teórica",
    sub: "El teórico en 1 semana",
    points: [
      "Resumimos lo que está cayendo en el examen",
      "En 1 semana alcanzas un nivel alto de preparación",
      "Sigues con test de examen y apuras los tiempos para examinarte cuanto antes",
    ],
    highlight: true,
    badge: "El más rápido",
  },
  {
    icon: MonitorPlay,
    title: "Plataforma online",
    sub: "Clases grabadas + test de examen",
    points: [
      "Todos los temas grabados y colgados en la plataforma",
      "Test de examen ilimitados, como los de la DGT",
      "Estudia cuando quieras, desde donde quieras",
    ],
    highlight: false,
  },
];

const PASOS = [
  { n: "1", t: "Te matriculas y eliges modalidad", d: "Presencial en el aula o intensivo online a tu medida. Te asesoramos según tu disponibilidad y tu fecha objetivo de examen." },
  { n: "2", t: "Intensivo: 1 semana a fondo", d: "Condensamos el temario y nos centramos en lo que está cayendo en el examen. Sales con un nivel alto de preparación." },
  { n: "3", t: "Test de examen hasta dominarlos", d: "Pasas a la fase de test en nuestra plataforma online, con preguntas del estilo oficial DGT y seguimiento de tus fallos." },
  { n: "4", t: "Apuras los tiempos y a examen", d: "En cuanto tus resultados son estables, pedimos fecha. Nuestro objetivo: que vayas a examen lo antes posible y a la primera." },
];

const FAQ = [
  { q: "¿Qué es el curso intensivo de teórica?", a: "Es un formato condensado en el que en 1 semana repasamos todo el temario centrado en lo que está cayendo en el examen de la DGT. Al terminar tienes un nivel alto de preparación y pasas directamente a la fase de test de examen, lo que te permite apurar los tiempos para examinarte cuanto antes." },
  { q: "¿En cuánto tiempo puedo presentarme al examen teórico?", a: "Con el intensivo, en 1 semana alcanzas el nivel para empezar con los test de examen. El tiempo total hasta el examen depende de tu dedicación con los test y de las fechas disponibles en Tráfico, pero es la vía más rápida que ofrecemos: muchos alumnos van a examen en pocas semanas desde que empiezan." },
  { q: "¿Cómo funciona el intensivo online a mi medida?", a: "Tenemos todos los temas grabados y colgados en nuestra plataforma online. Tú decides el ritmo: si quieres apurar, puedes ver todos los vídeos que quieras al día y hacerte tu propio intensivo personalizado, combinándolo con test de examen ilimitados." },
  { q: "¿Qué diferencia hay entre las tres modalidades de teórica?", a: "Clases tradicionales: clases presenciales diarias con profesor, ideales si prefieres rutina y resolver dudas en el aula. Plataforma online: temas grabados y test de examen para estudiar a tu ritmo. Intensivo: resumen de 1 semana centrado en lo que cae en el examen, presencial u online, pensado para ir a examen lo antes posible. Puedes combinarlas." },
  { q: "¿El intensivo vale para el carnet de coche y el de moto?", a: "Sí. La teórica común (permiso B y motos A1/A2) se prepara con cualquiera de las tres modalidades. Si tu permiso requiere teórica específica, te lo indicamos al matricularte y te preparamos también esa parte." },
  { q: "¿Necesito venir a un centro para hacer el intensivo?", a: "No necesariamente. Puedes hacer el intensivo presencial en nuestros centros o hacértelo online a tu medida con los temas grabados de la plataforma. Muchos alumnos combinan ambas opciones." },
  { q: "¿Cuánto cuesta el curso intensivo de teórica en Vigo?", a: "Depende del permiso y de la modalidad que elijas. Pídenos presupuesto sin compromiso por WhatsApp al 637 570 075, llamando al 986 29 24 55 o desde el formulario de contacto, y te lo preparamos en el día." },
  { q: "¿Dónde se imparte el intensivo presencial?", a: "En nuestros centros de Vigo y su área: Plaza América, Coruxo, Panxón, Nigrán y A Cañiza. Al matricularte te confirmamos horarios y el centro con el próximo intensivo disponible." },
];

export default function TeoricaIntensivaPage() {
  useSeo({
    title: "Curso Intensivo de Teórica en Vigo | Aprueba en 1 semana",
    description: "Prepárate el teórico en 1 semana con el curso intensivo en Vigo: resumimos lo que cae en el examen. También clases tradicionales y plataforma online.",
    canonical: `${SITE_URL}/curso-intensivo-teorica-vigo`,
    ogImage: "/assets/og/curso-intensivo-teorica-vigo.jpg",
    ogImageAlt: "Curso intensivo de teórica en Vigo — Autoescuelas Faro",
  });

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid="teorica-intensiva-page">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Course",
        name: "Curso intensivo de teórica en Vigo",
        description: "Curso intensivo para preparar el examen teórico de la DGT en 1 semana, con resumen de lo que está cayendo. Modalidades presencial y online con test.",
        provider: { "@type": "Organization", name: "Autoescuelas Faro", url: SITE_URL },
        hasCourseInstance: { "@type": "CourseInstance", courseMode: "Blended" },
        availableLanguage: "es",
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }} />

      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Breadcrumbs items={[{ label: "Permisos", to: "/permisos" }, { label: "Intensivo de teórica" }]} />
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">Teórica en Vigo · 3 modalidades</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
          Curso intensivo de teórica en Vigo: el teórico en 1 semana
        </h1>
        <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
          ¿Tienes prisa por sacarte el carnet? Con nuestro <strong>intensivo de teórica</strong> resumimos{" "}
          <strong>lo que está cayendo en el examen</strong> y en <strong>1 semana</strong> consigues un nivel alto de
          preparación. Después sigues con los <strong>test de examen</strong> de nuestra plataforma y apuras los tiempos
          para ir a examen cuanto antes. Y si lo prefieres, te haces el intensivo <strong>online a tu medida</strong>:
          todos los temas grabados, los ves a tu ritmo.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href={WA} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-sm" data-testid="ti-cta-whatsapp">
            <Zap size={15} /> Quiero el intensivo
          </a>
          <a href="/#contacto" className="btn-pill btn-outline text-sm" data-testid="ti-cta-contacto">
            <CalendarClock size={15} /> Pide información
          </a>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STATS.map((f) => (
            <div key={f.k} className="rounded-2xl bg-[var(--faro-blue)] text-white p-4">
              <f.icon size={18} className="text-[var(--faro-yellow)]" />
              <div className="font-display font-black text-2xl mt-2">{f.k}</div>
              <div className="text-xs text-white/80 mt-0.5 leading-snug">{f.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl overflow-hidden border border-[rgba(30,42,143,0.12)]">
          <img
            src="/assets/curso-intensivo-teorica-vigo.webp"
            alt="Clase intensiva de teórica en un aula de Autoescuelas Faro, con el profesor explicando una señal en pantalla"
            width="1200" height="805" loading="eager" fetchPriority="high"
            className="w-full h-auto"
          />
        </div>

        <section className="mt-12">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Tres formas de prepararte la teórica</h2>
          <p className="mt-2 text-[var(--faro-text-soft)]">Elige la que mejor encaje contigo, o combínalas.</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {MODALIDADES.map((m) => (
              <div
                key={m.title}
                data-testid={`ti-modalidad-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative rounded-2xl p-5 flex flex-col ${m.highlight ? "bg-[var(--faro-blue)] text-white border-2 border-[var(--faro-yellow)] shadow-xl" : "bg-white border border-[rgba(30,42,143,0.12)]"}`}
              >
                {m.badge && (
                  <span className="absolute -top-3 right-4 bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full">{m.badge}</span>
                )}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.highlight ? "bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)]" : "bg-[var(--faro-blue)] text-white"}`}>
                  <m.icon size={20} />
                </div>
                <h3 className="font-display font-bold text-lg mt-3">{m.title}</h3>
                <div className={`text-xs font-bold uppercase tracking-wider mt-0.5 ${m.highlight ? "text-[var(--faro-yellow)]" : "text-[var(--faro-red)]"}`}>{m.sub}</div>
                <ul className={`mt-3 space-y-2 text-sm flex-1 ${m.highlight ? "text-white/90" : "text-[var(--faro-text-soft)]"}`}>
                  {m.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${m.highlight ? "bg-[var(--faro-yellow)]" : "bg-[var(--faro-red)]"}`} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl bg-[var(--faro-bg)] border border-[rgba(30,42,143,0.12)] p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[var(--faro-red)] text-white flex items-center justify-center shrink-0">
              <PlayCircle size={24} />
            </div>
            <div>
              <h2 className="font-display font-black text-2xl sm:text-3xl">Tu intensivo online, a tu medida</h2>
              <p className="mt-2 text-[var(--faro-text-soft)]">
                Tenemos <strong>todos los temas grabados</strong> y colgados en nuestra plataforma online. Si quieres apurar,
                ve todos los vídeos que quieras al día y hazte tu <strong>intensivo personalizado</strong> sin horarios:
                tú decides cuándo y a qué velocidad. Al terminar cada tema, test de examen para fijar lo aprendido.
              </p>
              <a href={WA} target="_blank" rel="noreferrer" className="mt-4 inline-flex btn-pill btn-primary text-sm" data-testid="ti-cta-online">
                Pide acceso a la plataforma <ArrowRight size={15} />
              </a>
            </div>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Cómo funciona: de cero a examen</h2>
          <div className="mt-6 space-y-4">
            {PASOS.map((p) => (
              <div key={p.n} className="flex items-start gap-4 rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5" data-testid={`ti-paso-${p.n}`}>
                <div className="w-9 h-9 rounded-full bg-[var(--faro-blue)] text-white font-display font-black flex items-center justify-center shrink-0">{p.n}</div>
                <div>
                  <h3 className="font-bold text-[var(--faro-blue-deep)]">{p.t}</h3>
                  <p className="text-sm text-[var(--faro-text-soft)] mt-1">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12" id="faq">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Preguntas frecuentes</h2>
          <div className="mt-6 space-y-3">
            {FAQ.map((f) => (
              <details key={f.q} className="group rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
                <summary className="font-bold text-[var(--faro-blue-deep)] cursor-pointer list-none flex items-start justify-between gap-3">
                  {f.q}
                  <ArrowRight size={16} className="mt-1 shrink-0 transition-transform group-open:rotate-90 text-[var(--faro-red)]" />
                </summary>
                <p className="mt-3 text-sm text-[var(--faro-text-soft)] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-12 rounded-2xl bg-[var(--faro-blue)] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-display font-bold text-xl">¿Empezamos esta semana?</h3>
            <p className="text-white/80 text-sm mt-1">Cuéntanos tu fecha objetivo y te decimos el plan más rápido para llegar.</p>
          </div>
          <a href={WA} target="_blank" rel="noreferrer" data-testid="ti-cta-final" className="btn-pill bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] hover:brightness-95">
            Hablar por WhatsApp
          </a>
        </div>

        <div className="mt-8 text-sm text-[var(--faro-text-soft)]">
          También te puede interesar:{" "}
          <Link to="/carnet-coche-vigo" className="font-bold text-[var(--faro-blue)] underline underline-offset-2">carnet de coche en Vigo</Link>,{" "}
          <Link to="/carnet-moto-vigo" className="font-bold text-[var(--faro-blue)] underline underline-offset-2">carnet de moto</Link> o{" "}
          <Link to="/examen" className="font-bold text-[var(--faro-blue)] underline underline-offset-2">hazte un test teórico gratis</Link>.
        </div>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
